import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { createCheckout, gbp } from "../lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, setPlan, subtotal, unitPrice } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      // Stripe Checkout can't mix one-time + subscription in a single session — surface a friendly error early.
      const plans = new Set(items.map((i) => i.plan));
      if (plans.has("onetime") && plans.has("subscribe")) {
        toast.error("Please check out one-time and subscription items separately.");
        setLoading(false);
        return;
      }
      const payload = {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity, plan: i.plan })),
        origin_url: window.location.origin,
        email: email || null,
      };
      const res = await createCheckout(payload);
      window.location.href = res.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            className="fixed inset-0 z-[80] bg-forest-deep/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed top-0 right-0 z-[85] h-full w-full max-w-md bg-offwhite flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-tan">
              <div className="flex items-baseline gap-2">
                <h2 className="font-sans font-extrabold text-xl tracking-tight text-ink">Your bag</h2>
                <span className="font-serif italic text-faded">{items.length} item{items.length !== 1 ? "s" : ""}</span>
              </div>
              <button data-testid="close-cart-button" onClick={() => setOpen(false)} className="p-2 text-ink" aria-label="Close cart">
                <X strokeWidth={1.5} className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-20">
                  <ShoppingBag className="w-10 h-10 text-faded" strokeWidth={1} />
                  <p className="font-serif italic text-lg text-faded">Your bag is empty.</p>
                  <button onClick={() => setOpen(false)} className="text-sm font-bold uppercase tracking-widest text-forest underline underline-offset-4">
                    Shop the range
                  </button>
                </div>
              ) : (
                <ul className="flex flex-col gap-5">
                  {items.map((i) => (
                    <li key={`${i.slug}-${i.plan}`} data-testid={`cart-item-${i.slug}-${i.plan}`} className="flex gap-4">
                      <div className="w-20 h-24 rounded-xl overflow-hidden bg-tan/60 shrink-0">
                        <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <h3 className="font-sans font-bold text-sm text-ink leading-tight">{i.name}</h3>
                          <button data-testid={`remove-${i.slug}-${i.plan}`} onClick={() => removeItem(i.slug, i.plan)} className="text-faded hover:text-destructive shrink-0" aria-label="Remove">
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                        <button
                          data-testid={`toggle-plan-${i.slug}`}
                          onClick={() => setPlan(i.slug, i.plan, i.plan === "subscribe" ? "onetime" : "subscribe")}
                          className={`mt-1 text-[11px] font-bold tracking-wide px-2.5 py-1 rounded-full transition-colors ${
                            i.plan === "subscribe" ? "bg-forest text-cream" : "bg-tan text-faded"
                          }`}
                        >
                          {i.plan === "subscribe" ? "Subscribe & Save -15%" : "One-time · tap to save 15%"}
                        </button>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-3 bg-tan/60 rounded-full px-2 py-1">
                            <button data-testid={`dec-${i.slug}-${i.plan}`} onClick={() => updateQty(i.slug, i.plan, -1)} className="text-ink" aria-label="Decrease">
                              <Minus className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                            <span className="text-sm font-bold w-4 text-center">{i.quantity}</span>
                            <button data-testid={`inc-${i.slug}-${i.plan}`} onClick={() => updateQty(i.slug, i.plan, 1)} className="text-ink" aria-label="Increase">
                              <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                          </div>
                          <span className="font-sans font-bold text-sm text-ink">{gbp(unitPrice(i) * i.quantity)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-tan px-6 py-6 space-y-4">
                <div className="flex justify-between items-baseline">
                  <span className="font-serif italic text-faded">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-sans font-black text-2xl text-ink">{gbp(subtotal)}</span>
                </div>
                <input
                  data-testid="checkout-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email for order updates (optional)"
                  className="w-full rounded-full border border-tan bg-cream px-5 py-3 text-sm outline-none focus:border-forest transition-colors"
                />
                <button
                  data-testid="checkout-button"
                  onClick={checkout}
                  disabled={loading}
                  className="group w-full flex items-center justify-center gap-3 rounded-full bg-forest text-cream px-6 py-4 text-sm font-bold tracking-widest uppercase transition-colors duration-300 hover:bg-forest-deep disabled:opacity-60"
                >
                  {loading ? "Preparing checkout…" : "Secure checkout"}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                </button>
                <p className="text-center text-[11px] text-faded tracking-wide">
                  Apple Pay · Google Pay · Card — one-click via Stripe
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
